/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import {
    FormValidationAlert,
    NotificationsWrapper,
    zipCodeValidator,
} from 'sarsaparilla';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { PassTypeProps, ReservationProps } from '../props';
import RenderIf from '../utils/JSXUtils';

import {
    optionSelectMessageFormatter,
    optionSelectInputId,
} from './errorHandling/messageUtils';
import { VehicleTypes, WatercraftTypes } from '../constants';

/**
 * NOTE: This component is used by ui-widget. We will need to check ui-widget whenever we do a change here
 */
export default class PassSelectionError extends React.PureComponent {
    static propTypes = {
        validationErrors: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string))
            .isRequired,
        submitAttempted: PropTypes.bool.isRequired,
        needToKnowChecked: PropTypes.bool.isRequired,
        errors: PropTypes.shape({
            reservations: PropTypes.arrayOf(
                PropTypes.shape({
                    error_code: PropTypes.string,
                    error_message: PropTypes.string,
                })
            ),
        }).isRequired,
        passTypes: PropTypes.arrayOf(PassTypeProps).isRequired,
        reservations: PropTypes.arrayOf(ReservationProps).isRequired,
    };

    hasErrorMessage = (errs = {}) => {
        const review = Object.entries(errs).filter(
            ([, message]) => !isEmpty(message) && !isNil(message)
        );
        return !isEmpty(review);
    };

    formatError(errorMessage, passType) {
        if (errorMessage.includes('Unknown or missing equipment type')) {
            return 'Please select a vehicle type.';
        }
        const split = errorMessage.split(': ');
        let header = '';
        let message = errorMessage;
        if (split.length === 3) {
            header = split[1];
            message = `: ${split[2]}`;
        }
        if (split.length === 2) {
            message = split[1];
        }
        if (message.includes('option-select')) {
            const optSelectMsg = optionSelectMessageFormatter(errorMessage, passType);
            if (optSelectMsg !== null) {
                return optSelectMsg;
            }
        }
        return `${header}${message}`;
    }

    errorObject(error, displayNumber, passType) {
        return {
            inputId: this.inputId(displayNumber, error, passType),
            errorText: this.formatError(error, passType),
        };
    }

    errorSectionHeader(displayNumber, displayName) {
        return `${displayNumber}. ${displayName.toUpperCase()}`;
    }

    inputId(displayNumber, field, passType) {
        if (field.includes('First Name')) {
            return `holder-first-name-${displayNumber}`;
        }
        if (field.includes('Last Name')) {
            return `holder-last-name-${displayNumber}`;
        }

        if (field.includes('Company Name')) {
            return `holder-company-name-${displayNumber}`;
        }

        if (field.includes('Email Confirmation')) {
            return `holder-email-confirmation-${displayNumber}`;
        }
        if (field.includes('Email')) {
            return `holder-email-${displayNumber}`;
        }
        if (
            field.includes('vehicle-type') ||
            field.includes('Unknown or missing equipment type')
        ) {
            return `vehicle-type-${displayNumber}`;
        }
        if (Object.keys(VehicleTypes).some((key) => field.includes(key))) {
            return `vehicle-license-${displayNumber}`;
        }
        if (Object.keys(WatercraftTypes).some((key) => field.includes(key))) {
            return `watercraft-license-${displayNumber}`;
        }

        if (field.includes('start-date')) {
            return `start-date-${displayNumber}-toggle`;
        }

        if (field.includes('bus-number')) {
            return `bus-number-${displayNumber}`;
        }

        if (field.includes('number-of-passengers')) {
            return `number-of-passengers-${displayNumber}`;
        }

        if (field.includes('duration-of-visit')) {
            return `duration-of-visit-${displayNumber}`;
        }

        if (field.includes('option-select')) {
            return optionSelectInputId(displayNumber, field, passType);
        }
        if (field.includes('attendee')) {
            const el = document.querySelector('.attendee-info-form input');
            return el?.id || '';
        }
        if (field.includes('Zip Code')) {
            return `holder-zip-code-${displayNumber}`;
        }
        if (field.includes('non_resident_selector')) {
            return `non_resident_selector_${displayNumber}`;
        }
        return `${displayNumber}`;
    }

    // renders lists of errors associated with each reservation
    renderReservationErrors() {
        const {
            validationErrors,
            submitAttempted,
            needToKnowChecked,
            reservations,
            passTypes,
        } = this.props;

        const errorsObject = {};
        validationErrors.forEach((errors, index) => {
            if (!errors || errors.length === 0) {
                return;
            }
            let passName = '';
            let passType = null;
            if (passTypes) {
                passType = passTypes.find(
                    ({ id }) => id === reservations[index]?.pass_type_id
                );
                if (passType && passType.name) {
                    passName = passType.name;
                }
            }
            errorsObject[this.errorSectionHeader(index + 1, passName)] = errors.map(
                (error) => this.errorObject(error, index + 1, passType)
            );
        });

        const facilityHasNonResidentFees = passTypes?.some(
            (passType) => passType?.features?.non_resident_fee
        );

        if (submitAttempted) {
            reservations.forEach((item, index) => {
                const passType = passTypes.find(
                    ({ id }) => id === reservations[index]?.pass_type_id
                );
                const sectionHeader = this.errorSectionHeader(index + 1, passType?.name);

                if (
                    item?.holder_info?.email_confirmation &&
                    item?.holder_info?.email !== item?.holder_info?.email_confirmation
                ) {
                    errorsObject[sectionHeader] = [
                        ...(errorsObject[sectionHeader] || []),
                        this.errorObject(
                            'Email Confirmation: Email and confirmation do not match.',
                            index + 1,
                            passType?.name
                        ),
                    ];
                }

                // Validate that the residency attestation question is answered
                // for pass types that have active non resident fees
                if (
                    passType?.features?.non_resident_fee?.fee &&
                    !item?.features?.non_resident_fee?.answered
                ) {
                    errorsObject[sectionHeader] = [
                        ...(errorsObject[sectionHeader] || []),
                        this.errorObject(
                            'non_resident_selector: Please answer the U.S. citizenship / residency question to proceed to cart',
                            index + 1,
                            passType?.name
                        ),
                    ];
                }

                if (!item?.holder_info?.zip_code) {
                    errorsObject[sectionHeader] = [
                        ...(errorsObject[sectionHeader] || []),
                        this.errorObject(
                            'Zip Code: Zip Code: Must not be empty.',
                            index + 1,
                            passType?.name
                        ),
                    ];
                } else if (
                    facilityHasNonResidentFees &&
                    passType?.duration === 'annual' &&
                    !zipCodeValidator(item?.holder_info?.zip_code).isValid
                ) {
                    // Validate that the zip code is a US based zip code for annual passes
                    // for facilities that have active non resident fees
                    errorsObject[sectionHeader] = [
                        ...(errorsObject[sectionHeader] || []),
                        this.errorObject(
                            'Zip Code: A valid U.S. Zip Code is required',
                            index + 1,
                            passType?.name
                        ),
                    ];
                }
            });

            if (!needToKnowChecked) {
                errorsObject.GENERAL = [
                    {
                        inputId: 'need-to-know-checkbox',
                        errorText:
                            'Consent Checkbox: Please read and agree to the important information below.',
                    },
                ];
            }
        }

        return errorsObject;
    }

    render() {
        const { errors } = this.props;
        return (
            <div className="pass-selection-error-wrapper">
                <div className="rec-order-details-error">
                    <FormValidationAlert errors={this.renderReservationErrors()} />
                </div>
                {RenderIf(
                    this.props.submitAttempted &&
                        Object.keys(errors).length &&
                        this.hasErrorMessage()
                )(
                    <NotificationsWrapper noticeType="error" closeButton={false}>
                        <div className="rec-notification-title">Error:</div>
                        <ul>
                            {Object.entries(errors).map(([key, message]) => (
                                <li key={key}>{message}</li>
                            ))}
                        </ul>
                    </NotificationsWrapper>
                )}
            </div>
        );
    }
}
